// 던전 흐름: 마을↔던전 이동, 방 잠금·웨이브·정리, 보스방, 결과·보상, QA 픽스처 명령.
// D3a: 판 하나 = 스테이지 계획(content/stages.ts StagePlan) 하나. 지도(levels.ts)는 방 자리·문·후열을, 계획은 방 종류·웨이브·보스·배율을 정한다.
// 스테이지 판은 클리어 때 진행(game/progress.ts)을 기록하고, n-10 첫 클리어 뒤 마을로 돌아가면 다음 마을 이동 연출을 튼다.
import { CLEAR_HOLD, HERO, MOB_STATS, MOBS, REWARD, ROOM_HEAL, STARS, WAVE_SPAWN, ms } from "../shared/config";
import { Ev } from "../shared/events";
import { MAPS, inRect, isRegionId, villageOf, type MapDef, type RegionId } from "../shared/levels";
import { gridOfMap } from "../shared/stageMaps";
import { BossState, HeroAct, MOB_KIND_COUNT, MobKind, MobState, Scene, combatScene, type SceneId } from "../shared/types";
import { MOB_ROLE_BODY, MOB_ROLE_COUNT } from "../shared/mobRoles";
import type { MapGrid } from "../shared/levels";
import { XP_BOSS, XP_CLEAR, XpCause } from "../shared/growth";
import { REGIONS } from "../shared/regions";
import { BOSS_DEFS } from "../shared/content/bosses";
import { clearMult, firstClearReward } from "../shared/content/stageRewards";
import { anyStagePlan, baseStageOf, freePlan, nextStageOf, parseAnyStage, raidPlan, stageLabel, type RoomPlan, type StagePlan } from "../shared/content/stages";
import { PLAYABLE_RAIDS, RAID_XP_MULT, WYVERN, parseRaidStage, raidDef, raidStageKey, hardUnlocked, raidFirstId, raidHardDef, raidReceiptId, parseRaidReceipt, type RaidDiff, type RaidId } from "../shared/content/raid";
import { raidFirstReward, raidReward } from "../shared/content/raidReward";
import { raidNextN, raidServerFirst, raidUnifiedDone, raidWeekReceipts } from "./raidLedger";
import { settleDaily } from "./dailyPay";
import { parseCardLoot, rollCards, validCardLoot } from "../shared/content/cardReward";
import { settleCardLoots } from "./cardSettle";
import type { HeroId } from "../shared/content/types";
import type { AnyStageId, HardStageId, HellStageId, StageId } from "../shared/content/types";
import { HitSource } from "./combat";
import type { GameSim } from "./game";
import { rewardId, type RewardResult } from './save';
import { firstGoldId } from './owedReward';
import type { GearOp } from "../shared/gearLedger";
import { makeOwed, owedGearOps, type OwedReward } from "./owedReward";

const GATE_TICKS = ms(400);
/** 마을 이동 연출 길이(PRD 2.2 이동 연출: 짧은 페이드 + 가장자리 지역 제목 카드). 이 동안 영웅 입력을 막는다 */
export const TRAVEL_TICKS = ms(2600);
/** 마을 목표 문구(마을 번호 순서). 마을②~④는 문 자리가 마을마다 달라 시작 자리 기준 방위로 말한다(STAGE-WORLD-PRD 5.4, 퀘스트 창 villageDoorGuide와 같은 방위) */
const TOWN_OBJECTIVE = [
  "북쪽 두 문 중 하나로: 왼쪽 이끼 유적(입문) · 오른쪽 잿불 협곡(도전)",
  "던전 문: 북서쪽 잿불 협곡 · 북동쪽 서리 묘지",
  "던전 문: 북쪽 언덕길 서리 묘지 · 동쪽 잔교 끝 가라앉은 사원",
  "던전 문: 서쪽 널판 다리 건너 가라앉은 사원",
];
/** 방 안으로 이만큼 들어와야 전투가 시작된다(m) */
const ROOM_ENTER_INSET = 2;
/** 다음 웨이브: 현재 웨이브 잔존 비율 이하 */
const WAVE_REMAIN_RATIO = 0.3;
/** 별: 5분 이내 클리어(ECONOMY 6장). 결과 보상 REWARD도 shared/config에서 온다. */
const STAR_TIME_TICKS = STARS.timeTicks;
export const StarBit = { Clear: 1, Time: 2, Health: 4 } as const;
/** 입구 후보 수(방 둘레를 고르게 나눈 점) */
const ENTRY_CANDIDATES = 16;
/** 지도가 방마다 정한 입구 후보(RoomDef.entries) 최대 수 */
const ENTRY_LIST_MAX = 32;
/** 입구 둘레에서 빈 자리를 찾는 시도 수 */
const ENTRY_TRIES = 6;

export class FlowController {
  readonly gateTarget = new Uint8Array(16);
  private readonly gateFrom = new Float32Array(16);
  private readonly gateTick = new Uint8Array(16);
  private readonly pendingMobs = new Uint8Array(MOB_KIND_COUNT);
  private pendingElites = 0;
  /** C2: 종류별로 아직 내보낼 직군 몹 수(칸 = kind × MOB_ROLE_COUNT + role)와 이번 웨이브 정예 접두 목록·낸 정예 수 */
  private readonly pendingRoles = new Uint8Array(MOB_KIND_COUNT * MOB_ROLE_COUNT);
  private eliteAffixes: readonly number[] | undefined;
  private elitesSpawned = 0;
  private waveTick = 0;
  private waveDelay = 0;
  private waveSize = 0;
  private waveAlive = 0;
  /** 이번 웨이브 입구(최대 3곳)와 다음 줄 등장까지 남은 세계 틱 */
  private readonly entryX = new Float32Array(3);
  private readonly entryZ = new Float32Array(3);
  private entryCount = 0;
  private spawnClock = 0;
  /** 종류를 번갈아 내보내는 커서(고블린·방패·멧돼지가 섞여 들어오게) */
  private kindCursor = 0;
  private readonly candX = new Float32Array(ENTRY_LIST_MAX);
  private readonly candZ = new Float32Array(ENTRY_LIST_MAX);
  private readonly candD = new Float32Array(ENTRY_LIST_MAX);
  private saveRevision = -1;
  private retryTicks = 0;
  private settleTicks = 0;
  /** 보상 카드 지급 정산이 필요하다(처음 틱·저장 확정·장부 저장 실패 뒤 재시도) */
  private cardSettleRetry = true;
  /** 이번 판 하드 진행 저장이 실패해 다시 써야 하는지(D3b) */
  private hardSavePending = false;
  /** 노말 진행 저장 쓰기 실패(PR #175 3차 리뷰): 하드와 같이 결과 저장 경고에 묶고 tickSave가 다시 쓴다 */
  private progressSavePending = false;
  /** 지금 판 계획(마을이면 직전 계획을 그대로 둔다 — 마을에는 방이 없다) */
  plan: StagePlan = freePlan("r1");
  /** 마을에서 잠긴 문 앞에 섰을 때 안내를 한 번만 바꾸려는 표시 */
  private lockedExit = -1;
  constructor(private readonly g: GameSim) {}

  /** 방 i의 계획(종류·웨이브·보스). 지도 방 자리와 같은 순서 */
  roomPlan(i: number): RoomPlan | undefined { return this.plan.rooms[i]; }

  /** 하드·헬 판의 진행 저장소(노말·레이드·자유 입장은 null) — 하드(D3b)와 헬(C10)은 같은 저장소 클래스를 키만 달리 쓴다 */
  private diffStore(diff: string): import("./hardProgress").HardProgressStore | null {
    return diff === "hard" ? this.g.hard : diff === "hell" ? this.g.hell : null;
  }
  /** 노말·하드·헬 어느 스테이지 id든 지금 열렸는지 */
  private anyUnlocked(id: string, diff: string): boolean {
    const st = this.diffStore(diff);
    return st ? st.unlocked(id) : this.g.progress.unlocked(id as StageId);
  }

  /**
   * 스테이지 입장(D3a). 열린 스테이지만 들어간다. 마을·타이틀, 또는 결과(보상 선택 뒤)·패배 화면에서.
   * 성공하면 true(잠김·잘못된 id·장면 거절은 false)
   */
  enterStage(id: string): boolean {
    const g = this.g, f = g.flow, p = parseAnyStage(id);
    // 하드(D3b)는 하드 진행·노말 지역 클리어로, 노말은 노말 진행으로 연다
    if (!p || !this.anyUnlocked(id, p.diff)) return false;
    if (f.scene !== Scene.Village && f.scene !== Scene.Title && f.scene !== Scene.Result && f.scene !== Scene.Defeat) return false;
    if (f.scene === Scene.Result && g.result.cleared && g.result.pickedCard < 0) return false;
    const region = MAPS[`r${p.region}` as RegionId];
    this.loadMap(region.id, id as AnyStageId);
    return (f.scene as number) === Scene.Dungeon;
  }

  /**
   * 워프(마을 이동): 도착했던 마을로 간다(이동 연출과 함께). 마을에서만.
   * n-10 첫 클리어 뒤 마을로 돌아올 때도 같은 연출을 쓴다(loadMap이 예약된 이동을 꺼낸다).
   */
  travel(town: number): boolean {
    const g = this.g, f = g.flow;
    if (f.scene !== Scene.Village || f.paused || f.travelTo > 0 || g.village.open) return false;
    const from = f.town;
    if (town === from || !g.progress.setTown(town, true)) return false;
    this.loadMap("village");
    // 연출 문구는 실제로 선 마을(loadMap이 다른 탭 기록을 합친 뒤의 f.town)을 따른다. loadMap이 이미 이동 연출을 틀었으면 다시 틀지 않는다(c018)
    if (f.scene === Scene.Village && f.travelTo === 0 && f.town !== from) this.startTravel(from, f.town);
    return true;
  }

  private startTravel(from: number, to: number): void {
    const f = this.g.flow;
    f.travelFrom = from; f.travelTo = to; f.travelTick = 0; f.travelTotal = TRAVEL_TICKS;
    this.g.heroController.stopForMenu();
    const e = this.g.emit(Ev.Travel); e.a = to; e.b = from; e.c = TRAVEL_TICKS;
  }

  /** 이동 연출 진행(고정 스텝, 마을). 끝나면 입력이 풀린다 */
  tickTravel(): void {
    const f = this.g.flow;
    if (f.travelTo <= 0) return;
    if (++f.travelTick >= f.travelTotal) { f.travelTo = 0; f.travelFrom = 0; f.travelTick = 0; f.travelTotal = 0; }
  }

  /**
   * custom(레인 F 필드·균열): 판 계획·지도·격자·장면·이름을 직접 준다(마을 문·스테이지 진행과 무관한 판). 나머지 판 준비(저장 정리·초기화)는 같은 경로
   */
  loadMap(id: MapDef["id"], stage: AnyStageId | RaidId | `${RaidId}.hard` | null = null, custom?: { readonly scene: SceneId; readonly plan: StagePlan; readonly map: MapDef; readonly grid: MapGrid; readonly label: string; readonly objective: string }): void {
    const g = this.g, f = g.flow;
    g.save.refresh();
    this.syncResult();
    // UI 외의 명령·중복 입력도 카드 보상을 버리고 결과를 초기화할 수 없다.
    if (f.scene === Scene.Result && g.result.cleared && g.result.pickedCard < 0) return;
    // 일일 던전 도중 떠남: 보물고·광산은 시도 소진 실패로 닫는다(탑은 도전 유지, game/dailyRun.ts)
    if (g.dailyRun.running) g.dailyRun.abandon();
    // 용사의 시련 도중 떠남: 실패도 보상도 없이 닫는다. 결과 화면의 '다시 도전'이 다른 판에서 시련을 잇지 않게 마지막 시련도 잊는다(입장은 loadMap 뒤에 다시 적는다)
    if (g.trialRun.running) g.trialRun.abandon();
    g.trialRun.forget();
    // 던전 도중 마을로 돌아가면 이번 판 EXP를 지금 기록한다(클리어·패배는 이미 기록했고 같은 판은 한 번만 지급한다)
    if (combatScene(f.scene)) g.growth.commitRun();
    // 일시적 저장 실패 뒤 화면을 떠날 때 미저장 보상만 다시 기록한다.
    g.save.flush();
    g.progress.refresh();
    g.hard.refresh();
    g.hell.refresh();
    const restored = g.save.unpickedResult();
    // 미선택 보상 복원(새로고침): 그 판의 스테이지(노말 r…·하드 h…, 영수증 stage)를 되살려 '다시 도전'·'다음'이 같은 난이도·스테이지를 잇게 한다
    // (PR #176 리뷰 P1 — 이전엔 자유 입장 계획으로 복원돼 하드 판의 재도전이 노말 자유 입장이 됐다). 열려 있는 스테이지일 때만
    if (restored) { id = restored.region; stage = this.restoredStage(restored.stage); }
    // 판 계획: 필드·균열(custom)이면 그 계획, 레이드 id면 레이드 판(RAID-PRD), 스테이지를 주면 그 스테이지, 아니면 그 지역 지도 그대로(자유 입장). 마을은 직전 계획 유지(방이 없다)
    if (custom && !restored) this.plan = custom.plan;
    else if (isRegionId(id)) { const rs = stage ? parseRaidStage(stage) : null; this.plan = rs ? raidPlan(rs.raid, rs.diff) : stage ? anyStagePlan(stage as AnyStageId) : freePlan(id); }
    // 마을: 예약된 이동(n-10 첫 클리어)이 있으면 그 마을로 가며 연출을 튼다
    let travelFrom = 0;
    // 미선택 보상 결과를 복원하는 판(restored)에서는 이동을 시작하지 않는다: 보상 화면이 떠 있는 동안 연출·입력 잠금이 걸리지 않게(PR #160 리뷰 P2).
    // 카드를 고르고 마을로 갈 때 예약된 이동이 그대로 실행된다
    if (!restored && id === "village" && g.progress.travel > g.progress.town) {
      travelFrom = g.progress.town;
      g.progress.setTown(g.progress.travel);
    }
    f.town = g.progress.town;
    // 용사의 시련 목록(이룬·열린 시련): 마을에서 퀘스트 창이 읽는다. 다른 탭이 이룬 시련(영수증)도 여기서 합쳐진다
    if (id === "village") { g.trialRun.refresh(); g.jobRun.refresh(); }
    // 장비 장부: 이번 판 줍기를 저장하고 저장소 최신(다른 탭 장착 변경 포함)을 읽는다. 능력치 사본(입장 고정)보다 먼저 —
    // syncGrowth → refreshStats가 낡은 장비 합계로 입장 능력치를 고정하지 않게(PR #154 Codex 2차 P2)
    g.gear.flush(true);
    g.gear.refresh();
    g.profile.sync();
    g.growth.refresh();
    g.syncGrowth();
    // 지역 지도는 판 계획이 정한다(S2: 지역1 스테이지는 A·A′·M·B·B′·F). 스테이지 지도는 지도 객체별 판정 격자를 쓴다
    g.map = custom && !restored ? custom.map : id === "village" ? villageOf(f.town) : this.plan.map;
    // 마을②~④(S7)도 지도 객체마다 판정 격자가 다르다(마을①만 game.grids.village)
    g.grid = custom && !restored ? custom.grid : (id === "village" ? g.map !== MAPS.village : !!g.map.stageKey) ? gridOfMap(g.map) : g.grids[id];
    g.resetRun();
    f.travelTo = 0; f.travelFrom = 0; f.travelTick = 0; f.travelTotal = 0;
    this.resetClearHold(); this.clearPending = false;
    this.lockedExit = -1;
    // 바닥 드롭: 이전 판에 남은 드롭은 사라지고(주운 것은 장부에 있다) 새 판 드롭 문맥·씨앗을 정한다
    g.loot.begin(id);
    // 물약 쿨다운·감쇠·부활 깃털(판마다 1회)과 연산 id 판 nonce(D2c)
    g.consumables.begin();
    f.mapId = id; f.scene = custom && !restored ? custom.scene : id === "village" ? Scene.Village : Scene.Dungeon;
    f.sceneTick = 0; f.elapsedTicks = 0; f.roomIndex = -1; f.paused = false; f.sceneSerial++;
    f.roomState.fill(0);
    this.resetGates();
    const p = this.plan;
    f.stageId = id === "village" ? "" : p.raid ? raidStageKey(p.raid, p.raidDiff ?? "normal") : p.stage ?? "";
    f.stageKind = id === "village" ? "" : p.kind;
    f.stageLabel = id !== "village" && p.raid ? `레이드${p.raidDiff === "hard" ? " 하드" : ""} · ${g.map.name}` : id !== "village" && p.stage ? `${stageLabel(p.stage)} ${g.map.name}` : g.map.name;
    f.objectiveLabel = id === "village" ? TOWN_OBJECTIVE[f.town - 1] ?? TOWN_OBJECTIVE[0]
      : p.raid ? `다리를 건너 둥지로. ${WYVERN.name}이(가) 내려앉는다.`
      : p.kind === "mid" ? `중간보스 ${BOSS_DEFS[p.rooms[p.rooms.length - 1].boss!].name}이(가) 아레나에서 기다린다. 첫 전투방으로.`
      : p.kind === "final" ? `지역보스 ${BOSS_DEFS[p.rooms[p.rooms.length - 1].boss!].name}이(가) 아레나에서 기다린다. 첫 전투방으로.`
      : "첫 전투방으로 이동하세요.";
    if (custom && !restored) { f.stageLabel = custom.label; f.objectiveLabel = custom.objective; f.stageKind = custom.scene === Scene.Rift ? "rift" : "field"; }
    this.resetWaves();
    g.heroController.reset();
    g.snapHero(g.map.spawn.x, g.map.spawn.z);
    g.hero.facing = g.map.spawn.facing;
    g.mobController.reset();
    g.bossController.reset();
    // 레이드 판: 용병 셋을 영웅 뒤에 세우고 이번 주 보상 횟수를 정한다(RAID-PRD 2·3절). 아니면 레이드 상태를 비운다
    if (id !== "village" && p.raid) g.raid.prepare(p.raid, this.raidClears(p.raid), raidDef(p.raid).weeklyLimit, p.raidDiff ?? "normal");
    else { g.raid.reset(); this.refreshRaidWeek(); }
    g.result.reset();
    this.hardSavePending = false; this.progressSavePending = false;
    g.result.savePending = g.save.savePending || this.hardSavePending || this.progressSavePending;
    g.result.saveRecovered = g.save.recovered > 0;
    if (restored) {
      this.applyResult(restored);
      g.result.restored = true;
      // 복원한 판의 스테이지·다음 스테이지(하드는 다음 하드 스테이지가 열렸으면. 노말은 마을 이동 예약과 얽혀 이전처럼 비운다)
      g.result.stageId = stage ?? "";
      if (stage && !parseRaidStage(stage) && this.diffStore(this.plan.diff)) { const next = nextStageOf(stage as AnyStageId); g.result.nextStage = next && this.diffStore(this.plan.diff)!.unlocked(next) ? next : ""; }
      f.scene = Scene.Result; f.elapsedTicks = restored.clearTicks;
      f.roomState.fill(2, 0, g.map.rooms.length);
      f.roomIndex = g.map.rooms.length - 1;
      f.stageLabel = g.map.rooms[f.roomIndex].name;
      f.objectiveLabel = '저장된 보상 카드를 선택하세요.';
      g.hero.act = HeroAct.Victory;
      const at = g.map.rooms[f.roomIndex].bossAt;
      if (at) g.snapHero(at.x, at.z + 4);
    }
    g.emit(Ev.SceneChange).a = f.scene;
    if (travelFrom > 0) this.startTravel(travelFrom, f.town);
  }

  /** 보상 영수증의 스테이지 id → 지금 열린 스테이지면 그 id(노말·하드), 아니면 null(자유 입장 복원) */
  private restoredStage(id: string | undefined): AnyStageId | null {
    const p = parseAnyStage(id);
    if (!p) return null;
    return this.anyUnlocked(id!, p.diff) ? (id as AnyStageId) : null;
  }

  private applyResult(saved: RewardResult): void {
    const r = this.g.result;
    r.resultId = saved.id; r.cleared = true; r.stars = saved.stars; r.starMask = saved.starMask;
    r.clearTicks = saved.clearTicks; r.kills = saved.kills; r.maxCombo = saved.maxCombo;
    r.damageDealt = saved.damageDealt; r.cards.set(saved.cards); r.pickedCard = saved.pickedCard;
    // 카드 내용(선택 필드): 없거나 형식이 틀리면 전부 골드 카드(이전 영수증)
    const okLoot = validCardLoot(saved.cardLoot) && Array.isArray(saved.cardLoot);
    for (let i = 0; i < 3; i++) r.cardLoot[i] = okLoot ? saved.cardLoot![i] : '';
    const cnt = (v: unknown, hi: number): number => (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= hi ? v : 0);
    // 첫 클리어 골드는 고정 id 보너스 영수증으로 따로 지급되므로 결과 영수증 baseGold에는 없다 — 화면의 총 골드에는 합쳐 보인다
    r.gold = saved.baseGold + (saved.pickedCard >= 0 ? saved.cards[saved.pickedCard] : 0) + (saved.firstGold !== undefined ? cnt(saved.firstGold, 1e9) : 0);
    // 보상 내역 표시 필드(선택 필드: 이전 영수증에는 없어 기본값 그대로). 라이브 판의 값은 같은 값이라 덮어써도 변하지 않는다
    if (saved.clearMult !== undefined && Number.isFinite(saved.clearMult) && saved.clearMult > 0 && saved.clearMult <= 100) r.clearMult = saved.clearMult;
    if (saved.specialGold !== undefined) r.specialGold = cnt(saved.specialGold, 1e9);
    if (saved.firstGold !== undefined) r.firstGold = cnt(saved.firstGold, 1e9);
    if (saved.firstStones !== undefined) r.firstStones = cnt(saved.firstStones, 9999);
    if (saved.firstStoneTier !== undefined) r.firstStoneTier = cnt(saved.firstStoneTier, 3);
    if (Array.isArray(saved.firstGear)) {
      r.firstGear.length = 0;
      for (const gv of saved.firstGear) if (typeof gv === 'string' && gv.length <= 80 && r.firstGear.length < 8) r.firstGear.push(gv);
    }
    r.savePending = this.g.save.savePending || this.hardSavePending || this.progressSavePending;
    r.saveRecovered = this.g.save.recovered > 0;
  }

  /**
   * 첫 클리어 보상(강화석·장비)을 장비 장부에 넣고 바로 저장을 시도한다. 결과 화면 표시 필드를 채우고, 진행 저장에 적을 "받는 중" 기록을 돌려준다
   * (보상이 없으면 undefined). 장비 저장이 실패해도 기록은 남으므로 재접속 때 settleFirstClears가 같은 연산을 다시 넣는다.
   */
  private grantFirstClear(stage: string, fc: ReturnType<typeof firstClearReward> | null, r: GameSim["result"], firstId: string): OwedReward | undefined {
    if (!fc) return undefined;
    const g = this.g;
    r.firstStones = fc.stones; r.firstStoneTier = fc.stoneTier;
    const ops: GearOp[] = [];
    for (const op of g.loot.firstClearOps(stage as AnyStageId, fc)) {
      const res = g.gear.submit(op);
      ops.push(op);
      // 장부가 받은(ok) 연산, 가방이 가득 차 바로 정리된(auto_salvaged) 연산도 결과 화면 '받은 장비'에 올린다 — 같은 고정 id가 이미 들어 있어 duplicate로 거절된 장비는 표시하지 않는다(PR #175 5차 리뷰 P2)
      if (op.t === 'pickup' && (res.code === 'ok' || res.code === 'auto_salvaged')) r.firstGear.push(op.item.grade + ':' + op.item.def);
    }
    g.gear.flush(true);
    return makeOwed(ops, r.firstGold, firstId);
  }

  /** 첫 클리어 골드 보너스 영수증(스테이지 고정 id). 같은 id가 이미 있으면 기록하지 않아 몇 번을 불러도 골드는 한 번이다 */
  private recordFirstGold(id: string, region: RegionId, gold: number): void {
    if (!id || gold <= 0 || this.g.save.hasResult(id)) return;
    this.g.save.recordResult({ id, region, baseGold: gold, clearTicks: 0, maxCombo: 0, stars: 0, starMask: 0, kills: 0, damageDealt: 0, cards: [0, 0, 0], pickedCard: 0, bonus: true });
  }

  /**
   * 받는 중인 첫 클리어 보상 확정(노말·하드 진행 저장): 보상 연산을 같은 id로 장비 장부에 다시 넣고(이미 들어갔으면 장부가 거른다),
   * 장비 장부 저장이 확인되고 결과 영수증(첫 클리어 골드가 든)이 저장됐을 때만 수령(claimed)으로 바꾼다.
   * 재접속 뒤 영수증이 없으면(저장 전에 닫음) 수령 확정이 영속된 뒤에 골드를 직접 한 번 준다. 저장 확인 전에는 몇 번이든 다시 시도해도 보상은 정확히 한 번만 반영된다.
   * 틱에서는 일정 간격으로만 부른다(tickSave).
   */
  settleFirstClears(): void {
    const g = this.g;
    for (const store of [g.progress, g.hard, g.hell]) {
      if (!store.hasOwed) continue;
      for (const [key, owed] of store.owedEntries()) {
        for (const op of owedGearOps(owed)) g.gear.submit(op);
        if (!g.gear.flush(true)) continue;
        // 첫 클리어 골드 영수증(스테이지 고정 id)이 없으면(저장 전에 닫음) 같은 id로 다시 기록한다. 이미 있으면 기록하지 않으므로 중복 0.
        // 영수증 저장이 확인된 뒤에만 owed를 수령으로 바꾼다 — 진행 저장이 실패·읽기 전용이면 owed가 남아 다음 주기에 다시 시도한다
        if (owed.gold > 0 && owed.rid) this.recordFirstGold(owed.rid, `r${key.charAt(1)}` as RegionId, owed.gold);
        if (g.save.savePending) continue;
        store.settleOwed(key);
      }
    }
  }

  /**
   * 보상 카드 지급(골드가 아닌 카드): 저장으로 확정된 영수증(pickedCard ≥ 0)을 보고 장비 장부에 고정 id `cd:<영수증 id>`로 넣는다.
   * 장부 저장이 확인된 뒤에만 영수증에 지급 완료(cardGranted)를 적어, 닫힘·저장 실패 뒤에도 다음 접속에서 같은 연산으로 다시 시도한다(장부가 같은 id를 거른다).
   * 두 탭이 서로 다른 카드를 고르면 먼저 저장된 선택만 확정이므로 진 탭의 카드는 지급되지 않는다.
   */
  settleCardLoots(): void { this.cardSettleRetry = !settleCardLoots(this.g.save, this.g.gear); }

  /** 비동기 저장 결과는 고정 틱에서 화면·프로필에 반영한다. 실패는 2초마다 같은 정산을 재시도한다. */
  tickSave(): void {
    const g = this.g;
    if ((g.save.savePending || this.hardSavePending || this.progressSavePending) && ++this.retryTicks >= 120) {
      this.retryTicks = 0;
      if (g.save.savePending) g.save.flush();
      // 하드 진행 쓰기 실패(D3b, PR #176 리뷰 P2): 같은 주기로 다시 쓴다. 미래 버전(읽기 전용)이면 계속 경고로 남는다
      if (this.hardSavePending) this.hardSavePending = !((g.hard.lastSaveOk || g.hard.retrySave()) && (g.hell.lastSaveOk || g.hell.retrySave()));
      if (this.progressSavePending) this.progressSavePending = !g.progress.retrySave();
    }
    // 받는 중인 첫 클리어 보상: 1초마다 저장 확인을 다시 시도한다(재접속 직후 첫 확인도 여기서). 레이드 보상 정산도 같은 주기
    if (++this.settleTicks >= 60) { this.settleTicks = 0; if (g.progress.hasOwed || g.hard.hasOwed || g.hell.hasOwed) this.settleFirstClears(); this.settleRaids(); g.dailyRun.retryPending(); g.trialRun.retryPending(); settleDaily(g); if (this.cardSettleRetry) this.settleCardLoots(); }
    if (this.saveRevision !== g.save.revision) {
      this.saveRevision = g.save.revision; this.syncResult(); this.settleCardLoots(); g.profile.sync();
    }
    g.result.savePending = g.save.savePending || this.hardSavePending || this.progressSavePending || this.raidPayoutPending() || g.dailyRun.payoutPending || g.trialRun.payoutPending;
    g.result.saveRecovered = g.save.recovered > 0;
  }

  private syncResult(): void {
    const g = this.g, saved = g.save.getResult(g.result.resultId);
    if (saved && g.flow.scene === Scene.Result) {
      this.applyResult(saved);
      if (saved.pickedCard >= 0) g.flow.objectiveLabel = '보상 획득! 마을로 돌아가세요.';
    }
  }

  private resetGates(): void {
    const g = this.g, f = g.flow;
    f.gateOpen.fill(0); this.gateTarget.fill(0); this.gateFrom.fill(0); this.gateTick.fill(GATE_TICKS);
    for (let i = 0; i < g.map.gates.length; i++) {
      const open = g.map.gates[i].openAtStart ? 1 : 0;
      f.gateOpen[i] = open; this.gateTarget[i] = open; this.gateFrom[i] = open;
    }
  }

  private resetWaves(): void {
    const f = this.g.flow;
    f.wave = 0; f.waveCount = 0; f.objectiveKilled = 0; f.objectiveTotal = 0;
    this.pendingMobs.fill(0); this.pendingElites = 0;
    this.waveTick = 0; this.waveDelay = 0; this.waveSize = 0; this.waveAlive = 0;
    this.entryCount = 0; this.spawnClock = 0; this.kindCursor = 0;
  }

  step(): void {
    const g = this.g, f = g.flow, h = g.hero;
    if (f.scene === Scene.Village) {
      if (f.travelTo > 0) return;
      let inside = -1;
      for (let i = 0; i < g.map.exits.length; i++) if (inRect(g.map.exits[i].rect, h.x, h.z)) { inside = i; break; }
      if (inside < 0) { if (this.lockedExit >= 0) { this.lockedExit = -1; f.objectiveLabel = TOWN_OBJECTIVE[f.town - 1] ?? TOWN_OBJECTIVE[0]; } return; }
      const to = g.map.exits[inside].to;
      if (!isRegionId(to)) { this.loadMap(to); return; }
      // 지역 문: 그 지역의 고른 스테이지로 들어간다. 잠긴 지역은 들어가지 않고 안내만 바꾼다(문 앞 카드에도 잠김 표시)
      // 문 난이도가 하드이고 그 지역 하드가 열렸으면 고른 하드 스테이지(D3b)
      const stage = g.doorStage(to);
      if (stage) { this.loadMap(to, stage as AnyStageId); return; }
      if (this.lockedExit !== inside) {
        this.lockedExit = inside;
        const prev = REGIONS[`r${Math.max(1, Number(to.slice(1)) - 1)}` as RegionId];
        f.objectiveLabel = `${REGIONS[to].name}: ${prev.name} 지역보스(${Number(to.slice(1)) - 1}-10)를 쓰러뜨리면 열린다.`;
        g.emit(Ev.StageLocked).a = Number(to.slice(1));
      }
      return;
    }
    if (f.scene !== Scene.Dungeon) return;
    for (let i = 0; i < g.map.rooms.length; i++) {
      if (f.roomState[i] !== 0 || (i > 0 && f.roomState[i - 1] !== 2)) continue;
      if (inRect(g.map.rooms[i].area, h.x, h.z, ROOM_ENTER_INSET)) { this.enterRoom(i); break; }
    }
    const room = this.plan.rooms[f.roomIndex];
    if (room && room.kind === "battle" && f.roomState[f.roomIndex] === 1) {
      this.spawnPending();
      // 웨이브 간격은 세계 시간으로 센다(각성 섬광·컷인 중에는 멈춘다).
      if (g.worldTick) this.waveTick++;
      const pending = this.pendingCount();
      const lastWave = f.wave + 1 >= room.waves.length;
      if (pending === 0 && !lastWave && (this.waveAlive <= Math.floor(this.waveSize * WAVE_REMAIN_RATIO) || this.waveTick >= this.waveDelay)) this.startWave(f.wave + 1);
      // 마지막 방이 전투방이면(일반 스테이지의 아레나 수비대) 그 방 정리가 곧 스테이지 클리어다
      else if (pending === 0 && lastWave && g.mobController.living === 0 && !g.special.spawnBeforeClear()) { if (f.roomIndex === this.plan.rooms.length - 1) this.requestClear(); else this.clearRoom(); }
    }
    this.updateGates();
    if (this.plan.daily) g.dailyRun.step();
    if (g.trialRun.running) g.trialRun.step();
  }

  /** 방 이름 표시: 자유 입장은 지도 이름 그대로, 스테이지 판은 '3-7 무덤 들판'(지도 이름 앞 번호를 스테이지 번호로) */
  roomLabel(index: number): string {
    const room = this.g.map.rooms[index], rp = this.plan.rooms[index], p = this.plan;
    if (!room || !p.stage) return room ? room.name : "";
    const base = room.name.replace(/^\S+\s/, "");
    if (room.kind === "boss" && rp && rp.kind === "battle") return `${stageLabel(p.stage)} 아레나 수비대`;
    if (rp && rp.kind === "boss" && rp.boss) return `${stageLabel(p.stage)} ${BOSS_DEFS[rp.boss].name}`;
    return `${stageLabel(p.stage)} ${base}`;
  }

  private enterRoom(index: number): void {
    const g = this.g, f = g.flow, room = g.map.rooms[index], rp = this.plan.rooms[index];
    f.roomIndex = index; f.roomState[index] = 1; f.stageLabel = this.roomLabel(index);
    f.wave = 0; f.waveCount = rp.waves.length; f.objectiveKilled = 0; f.objectiveTotal = 0;
    this.setGate(room.gateIn, false);
    g.emit(Ev.RoomEnter).a = index;
    g.hazards.enterRoom();
    if (rp.kind === "boss" && this.plan.raid) {
      // 레이드(RAID-PRD 4절): 섬에 오르면 문이 닫히고 비룡이 내려앉는다
      f.objectiveTotal = 1; f.objectiveLabel = `${WYVERN.name}을(를) 쓰러뜨리세요. 경고 장판을 피하고 흩어져 불 장판을 줄이세요.`;
      const at = room.bossAt ?? { x: (room.area.x0 + room.area.x1) / 2, z: room.area.z0 + 8 };
      g.raid.enterBoss(at.x, at.z, room.area);
    } else if (rp.kind === "boss") {
      const key = rp.boss ?? "treant", def = BOSS_DEFS[key];
      f.objectiveTotal = 1; f.objectiveLabel = `${def.name}을(를) 처치하세요.`;
      const at = room.bossAt ?? { x: (room.area.x0 + room.area.x1) / 2, z: room.area.z0 + 8 };
      g.bossController.enter(at.x, at.z, key, this.plan.bossHp, this.plan.bossDamageMult, this.plan.bossExtras);
    } else {
      const room = rp;
      for (let i = 0; i < room.waves.length; i++) for (let k = 0; k < MOB_KIND_COUNT; k++) f.objectiveTotal += (room.waves[i].counts[k] ?? 0);
      f.objectiveLabel = "모든 몬스터를 처치하세요.";
      this.startWave(0);
    }
  }

  private startWave(index: number): void {
    const g = this.g, f = g.flow, wave = this.plan.rooms[f.roomIndex].waves[index];
    f.wave = index;
    this.waveSize = this.waveAlive = wave.counts[0] + wave.counts[1] + wave.counts[2] + (wave.counts[3] ?? 0);
    this.waveTick = 0;
    this.waveDelay = g.rng.int(MOBS.waveInterval[0], MOBS.waveInterval[1] + 1);
    this.pendingElites = wave.elites ?? 0;
    for (let k = 0; k < MOB_KIND_COUNT; k++) this.pendingMobs[k] = wave.counts[k] ?? 0;
    this.pendingRoles.fill(0);
    this.eliteAffixes = wave.eliteAffix; this.elitesSpawned = 0;
    if (wave.roles) for (const r of wave.roles) {
      // 몸이 안 맞는 조합·칸 수를 넘는 합은 버린다
      if (r.kind < 0 || r.kind >= MOB_KIND_COUNT || r.role <= 0 || r.role >= MOB_ROLE_COUNT || MOB_ROLE_BODY[r.role] !== r.kind) continue;
      let used = 0;
      for (let q = 1; q < MOB_ROLE_COUNT; q++) used += this.pendingRoles[r.kind * MOB_ROLE_COUNT + q];
      this.pendingRoles[r.kind * MOB_ROLE_COUNT + r.role] += Math.max(0, Math.min(r.count, this.pendingMobs[r.kind] - used));
    }
    this.chooseEntries(this.waveSize >= WAVE_SPAWN.threeEntriesAt ? 3 : 2);
    this.spawnClock = 0;
    const e = g.emit(Ev.WaveStart); e.a = index; e.b = this.waveSize;
    this.spawnPending();
  }

  /**
   * 입구 고르기: 방 둘레(벽에서 inset 안쪽)를 고르게 나눈 후보 중 걸을 수 있고 영웅에게서 먼 곳부터,
   * 서로 separation 이상 떨어진 count곳. 웨이브 시작에만 부른다(미리 만든 배열만 쓴다).
   */
  private chooseEntries(count: number): void {
    const g = this.g, room = g.map.rooms[g.flow.roomIndex], h = g.hero;
    this.entryCount = 0;
    if (!room) return;
    const ins = WAVE_SPAWN.inset;
    const x0 = room.area.x0 + ins, x1 = room.area.x1 - ins, z0 = room.area.z0 + ins, z1 = room.area.z1 - ins;
    const w = Math.max(0, x1 - x0), d = Math.max(0, z1 - z0), per = 2 * (w + d);
    let n = 0;
    const offset = g.rng.next();
    // 지도가 입구 후보를 정한 방(원형·굽은 방): 그 점에서 고른다(사각형 둘레가 바닥 밖이라 둘레 후보가 비기 때문)
    const listed = room.entries;
    if (listed) for (let j = 0; j < listed.length && j < ENTRY_LIST_MAX; j++) {
      const x = listed[j].x, z = listed[j].z;
      if (!g.walkable(x, z, 0.8, -1, true)) continue;
      this.candX[n] = x; this.candZ[n] = z; this.candD[n] = Math.hypot(x - h.x, z - h.z); n++;
    }
    else for (let j = 0; j < ENTRY_CANDIDATES; j++) {
      let s = ((j + offset) / ENTRY_CANDIDATES) * per, x: number, z: number;
      if (s < w) { x = x0 + s; z = z0; }
      else if ((s -= w) < d) { x = x1; z = z0 + s; }
      else if ((s -= d) < w) { x = x1 - s; z = z1; }
      else { s -= w; x = x0; z = z1 - s; }
      if (!g.walkable(x, z, 0.8, -1, true)) continue;
      this.candX[n] = x; this.candZ[n] = z; this.candD[n] = Math.hypot(x - h.x, z - h.z); n++;
    }
    // 먼 곳부터 고르되, 서로 너무 가까우면 건너뛴다(선택 정렬, 할당 없음)
    for (let pick = 0; pick < count; pick++) {
      let best = -1, bestD = -Infinity;
      for (let j = 0; j < n; j++) {
        if (this.candD[j] < 0) continue;
        let near = false;
        for (let e = 0; e < this.entryCount; e++) {
          if (Math.hypot(this.candX[j] - this.entryX[e], this.candZ[j] - this.entryZ[e]) < WAVE_SPAWN.separation) { near = true; break; }
        }
        if (near) continue;
        // 선호 거리에 가까울수록 높다. 최소 거리 미만 후보는 점수를 크게 깎는다(다른 곳이 없을 때만 쓰인다)
        const near12 = -Math.abs(this.candD[j] - WAVE_SPAWN.preferDistance) + g.rng.next() * 3;
        const score = this.candD[j] >= WAVE_SPAWN.minDistance ? near12 : this.candD[j] - 100;
        if (score > bestD) { bestD = score; best = j; }
      }
      if (best < 0) break;
      this.entryX[this.entryCount] = this.candX[best]; this.entryZ[this.entryCount] = this.candZ[best];
      this.candD[best] = -1;
      this.entryCount++;
    }
  }

  /** 남은 마릿수가 있는 다음 종류(커서부터 돌아가며). 없으면 -1 */
  private nextKind(): number {
    for (let s = 0; s < MOB_KIND_COUNT; s++) {
      const k = (this.kindCursor + s) % MOB_KIND_COUNT;
      if (this.pendingMobs[k] > 0) { this.kindCursor = (k + 1) % MOB_KIND_COUNT; return k; }
    }
    return -1;
  }

  /**
   * 줄지은 등장: interval 세계 틱마다 입구마다 한 마리씩 나온다. 자리가 없거나 용량이 차면
   * 다음 차례에 다시 시도해 마릿수를 잃지 않는다. 입구를 못 고른 방은 예전처럼 영웅 둘레에 낸다.
   */
  private spawnPending(): void {
    const g = this.g, room = g.map.rooms[g.flow.roomIndex];
    if (!room) return;
    if (this.pendingCount() === 0) return;
    if (this.entryCount > 0) {
      if (this.spawnClock > 0) { if (g.worldTick) this.spawnClock--; return; }
      this.spawnClock = WAVE_SPAWN.interval;
      for (let n = 0; n < this.entryCount * WAVE_SPAWN.perEntry; n++) {
        const e = n % this.entryCount;
        const k = this.nextKind();
        if (k < 0) return;
        const role = this.roleFor(k), elite = k !== MobKind.Shaman && this.pendingElites > 0, affix = elite ? this.nextAffix() : 0;
        let spawned = k === MobKind.Shaman && room.backline ? this.spawnBackline(role) : -1;
        for (let t = 0; t < ENTRY_TRIES && spawned < 0 && !(k === MobKind.Shaman && room.backline); t++) {
          const a = g.rng.range(-Math.PI, Math.PI), r = g.rng.range(0, WAVE_SPAWN.jitter);
          const x = this.entryX[e] + Math.sin(a) * r, z = this.entryZ[e] + Math.cos(a) * r;
          if (this.spawnFree(k, x, z)) spawned = g.mobController.spawn(k, elite, x, z, g.flow.wave, role, affix);
        }
        if (spawned < 0) { this.kindCursor = k; continue; }
        this.pendingMobs[k]--;
        if (role > 0) this.pendingRoles[k * MOB_ROLE_COUNT + role]--;
        if (elite) { this.pendingElites--; this.elitesSpawned++; }
      }
      return;
    }
    for (let k = 0; k < MOB_KIND_COUNT; k++) {
      while (this.pendingMobs[k] > 0) {
        const role = this.roleFor(k), elite = k !== MobKind.Shaman && this.pendingElites > 0, affix = elite ? this.nextAffix() : 0;
        let spawned = k === MobKind.Shaman && room.backline ? this.spawnBackline(role) : -1;
        for (let attempt = 0; attempt < 24 && spawned < 0 && !(k === MobKind.Shaman && room.backline); attempt++) {
          const angle = g.rng.range(-Math.PI, Math.PI), radius = g.rng.range(4, 9);
          const x = Math.max(room.area.x0 + 1, Math.min(room.area.x1 - 1, g.hero.x + Math.sin(angle) * radius));
          const z = Math.max(room.area.z0 + 1, Math.min(room.area.z1 - 1, g.hero.z + Math.cos(angle) * radius));
          if (this.spawnFree(k, x, z)) spawned = g.mobController.spawn(k, elite, x, z, g.flow.wave, role, affix);
        }
        // 용량·유효 위치가 부족하면 다음 틱에 다시 시도해 마릿수를 잃지 않는다.
        if (spawned < 0) return;
        this.pendingMobs[k]--;
        if (role > 0) this.pendingRoles[k * MOB_ROLE_COUNT + role]--;
        if (elite) { this.pendingElites--; this.elitesSpawned++; }
      }
    }
  }

  private pendingCount(): number {
    let n = 0;
    for (let k = 0; k < MOB_KIND_COUNT; k++) n += this.pendingMobs[k];
    return n;
  }

  /**
   * 이번에 내보낼 k종 몹의 직군(0 = 기본). 남은 마릿수 P 중 직군 R마리가 앞뒤로 고르게 섞이게 정한다(마지막 마리까지 R이 남지 않는다).
   * 직군이 둘 이상이면 남은 수가 많은 쪽부터. 난수를 쓰지 않아 직군 없는 웨이브의 난수열을 흔들지 않는다
   */
  private roleFor(k: number): number {
    let total = 0, best = 0, bestN = 0;
    for (let q = 1; q < MOB_ROLE_COUNT; q++) {
      const n = this.pendingRoles[k * MOB_ROLE_COUNT + q];
      total += n;
      if (n > bestN) { bestN = n; best = q; }
    }
    if (total === 0) return 0;
    const P = this.pendingMobs[k];
    return total >= P || (P - 1) % Math.ceil(P / total) === 0 ? best : 0;
  }

  /** 다음 정예의 접두(목록을 정예가 나온 순서대로 돌려 쓴다) */
  private nextAffix(): number {
    const list = this.eliteAffixes;
    return list && list.length > 0 ? list[this.elitesSpawned % list.length] : 0;
  }

  private spawnBackline(role = 0): number {
    const g = this.g, area = g.map.rooms[g.flow.roomIndex].backline!;
    let fallbackX = 0, fallbackZ = 0, found = false;
    for (let i = 0; i < 32; i++) {
      const x = g.rng.range(area.x0 + 0.5, area.x1 - 0.5), z = g.rng.range(area.z0 + 0.5, area.z1 - 0.5);
      if (!this.spawnFree(MobKind.Shaman, x, z)) continue;
      if (Math.hypot(x - g.hero.x, z - g.hero.z) >= WAVE_SPAWN.minDistance) return g.mobController.spawn(MobKind.Shaman, false, x, z, g.flow.wave, role);
      fallbackX = x; fallbackZ = z; found = true;
    }
    return found ? g.mobController.spawn(MobKind.Shaman, false, fallbackX, fallbackZ, g.flow.wave, role) : -1;
  }

  private spawnFree(kind: number, x: number, z: number): boolean {
    const g = this.g, m = g.mobs, radius = MOB_STATS[kind].radius;
    if (!g.walkable(x, z, radius, -1, true)) return false;
    for (let i = 0; i < m.capacity; i++) {
      if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying) continue;
      if (Math.hypot(m.x[i] - x, m.z[i] - z) < radius + g.mobController.radius(i) + 0.05) return false;
    }
    return Math.hypot(g.hero.x - x, g.hero.z - z) >= radius + HERO.radius;
  }

  spawnNear(kind: number, elite: boolean, x: number, z: number, wave: number): number {
    if (this.spawnFree(kind, x, z)) return this.g.mobController.spawn(kind, elite, x, z, wave);
    for (let ring = 1; ring <= 4; ring++) {
      for (let i = 0; i < 16; i++) {
        const angle = (i * Math.PI) / 8, px = x + Math.sin(angle) * ring, pz = z + Math.cos(angle) * ring;
        if (this.spawnFree(kind, px, pz)) return this.g.mobController.spawn(kind, elite, px, pz, wave);
      }
    }
    return -1;
  }

  mobKilled(index: number): void {
    const g = this.g, f = g.flow, room = this.plan.rooms[f.roomIndex];
    if (!room || room.kind !== "battle") return;
    // C2 졸개(소환·분열)는 목표 집계·웨이브 진행(waveAlive)에 들지 않는다. 방 정리는 싸울 수 있는 몹 0이라 졸개도 치워야 끝난다
    if (g.mobController.isMinion(index)) return;
    f.objectiveKilled++;
    if (g.mobController.waveId[index] === f.wave) this.waveAlive--;
  }

  /** 진행 중인 전투 방의 마지막 몹을 막 쓰러뜨렸는지(남은 등장 대기·다음 웨이브·싸울 수 있는 몹이 없다). KO 연출 판정용 */
  finalKill(): boolean {
    const g = this.g, f = g.flow, room = this.plan.rooms[f.roomIndex];
    if (!room || room.kind !== "battle" || f.roomState[f.roomIndex] !== 1) return false;
    const pending = this.pendingCount();
    return pending === 0 && f.wave + 1 >= room.waves.length && g.mobController.living === 0 && !g.special.dueBeforeClear();
  }

  private clearRoom(): void {
    const g = this.g, f = g.flow, room = g.map.rooms[f.roomIndex];
    f.roomState[f.roomIndex] = 2; g.hazards.reset(); f.objectiveLabel = "출구로 이동하세요.";
    const missing = Math.max(0, g.hero.maxHp - g.hero.hp);
    const amount = Math.floor(missing * ROOM_HEAL.missingRatio);
    if (amount > 0) {
      g.hero.hp += amount;
      const e = g.emit(Ev.HeroHeal); e.x = g.hero.x; e.z = g.hero.z; e.a = amount; e.b = 0;
    }
    g.emit(Ev.RoomClear).a = f.roomIndex;
    if (room.gateOut >= 0) this.setGate(room.gateOut, true);
  }

  setGate(index: number, open: boolean): void {
    if (index < 0) return;
    const target = open ? 1 : 0;
    if (this.gateTarget[index] === target) return;
    this.gateTarget[index] = target; this.gateFrom[index] = this.g.flow.gateOpen[index]; this.gateTick[index] = 0;
    const e = this.g.emit(Ev.Gate); e.a = index; e.b = target;
    if (!open) this.pushFromGate(index);
  }

  /** 문 애니메이션: GATE_TICKS 동안 선형 보간(실시간) */
  private updateGates(): void {
    const g = this.g;
    for (let i = 0; i < g.map.gates.length; i++) {
      if (this.gateTick[i] >= GATE_TICKS) continue;
      if (this.gateTarget[i] === 0) this.pushFromGate(i);
      const t = ++this.gateTick[i] / GATE_TICKS;
      g.flow.gateOpen[i] = t >= 1 ? this.gateTarget[i] : this.gateFrom[i] + (this.gateTarget[i] - this.gateFrom[i]) * t;
    }
  }

  /** 닫히는 문 셀의 영웅·몹을 좁은 축의 가까운 바닥 쪽으로 순간이동(prev 포함) */
  private pushFromGate(index: number): void {
    const g = this.g, rect = g.map.gates[index].rect, h = g.hero, m = g.mobs;
    for (let i = -1; i < m.capacity; i++) {
      if (i >= 0 && m.state[i] === MobState.Inactive) continue;
      const x = i < 0 ? h.x : m.x[i], z = i < 0 ? h.z : m.z[i];
      const radius = i < 0 ? HERO.radius : g.mobController.radius(i);
      if (x + radius <= rect.x0 || x - radius >= rect.x1 || z + radius <= rect.z0 || z - radius >= rect.z1) continue;
      let nx = x, nz = z;
      if (rect.z1 - rect.z0 <= rect.x1 - rect.x0) {
        const low = rect.z0 - radius - 0.02, high = rect.z1 + radius + 0.02;
        nz = z < (rect.z0 + rect.z1) / 2 ? low : high;
        if (!g.walkable(nx, nz, radius, index, i >= 0)) nz = nz === low ? high : low;
      } else {
        const low = rect.x0 - radius - 0.02, high = rect.x1 + radius + 0.02;
        nx = x < (rect.x0 + rect.x1) / 2 ? low : high;
        if (!g.walkable(nx, nz, radius, index, i >= 0)) nx = nx === low ? high : low;
      }
      if (!g.walkable(nx, nz, radius, index, i >= 0)) continue;
      if (i < 0) g.snapHero(nx, nz);
      else g.snapMob(i, nx, nz);
    }
  }

  /** 클리어 연출 대기 상태(판마다 비운다). started: 대기를 시작했다, released: 대기가 끝나 결과로 넘어가도 된다 */
  private holdStarted = false;
  private holdReleased = false;

  private resetClearHold(): void {
    const f = this.g.flow;
    this.holdStarted = false; this.holdReleased = false;
    f.clearHold = 0; f.clearHoldTotal = 0; f.clearFocusX = 0; f.clearFocusZ = 0;
  }

  /** 이 처치가 스테이지(레이드 제외 던전)의 마지막 방을 끝내는 일반 몹 처치인지(보스 없는 판의 마지막 몹). KO 연출을 보스급으로 키우는 판정 */
  endsStage(): boolean {
    return !this.plan.raid && this.g.flow.roomIndex === this.plan.rooms.length - 1 && this.finalKill();
  }

  /**
   * 클리어 연출 대기를 시작한다(보스 결정타 KO 또는 마지막 몹 처치). 이미 시작했으면 무시.
   * 대기 동안 영웅은 그대로 움직이고(조작 지연 없음), 카메라는 (x, z)의 쓰러지는 장면을 잡고, 바닥 드롭은 거두지 않는다.
   */
  beginClearHold(x: number, z: number, boss: boolean): void {
    const f = this.g.flow;
    if (this.holdStarted || f.scene !== Scene.Dungeon) return;
    this.holdStarted = true;
    f.clearHoldTotal = boss ? CLEAR_HOLD.bossTicks : CLEAR_HOLD.roomTicks;
    f.clearHold = f.clearHoldTotal; f.clearFocusX = x; f.clearFocusZ = z;
    // 보스가 쓰러졌으면 남은 경고 데칼·장판·예약 판정을 거둔다
    if (boss) { this.g.releaseAllTelegraphs(); }
  }

  /** 실제 틱 1회(히트스톱 중에도 센다). 시간이 다 되면 보스가 이미 쓰러졌을 때 곧바로 결과로 넘긴다 */
  tickClearHold(): void {
    const f = this.g.flow;
    if (f.clearHold <= 0 || f.scene !== Scene.Dungeon) return;
    if (--f.clearHold > 0) return;
    this.holdReleased = true;
    if (this.clearPending) this.stageClear();
  }

  /**
   * 마지막 적이 쓰러졌다(보스 사망 연출 끝·마지막 몹 처치): 곧바로 결과로 넘기지 않고 쓰러진 장면을 먼저 보여 준다.
   * 대기를 아직 안 했으면 시작하고, 끝나기 전에는 넘기지 않는다(끝나면 tickClearHold가 stageClear를 부른다). 대기가 끝났으면 바로 넘긴다
   */
  requestClear(): void {
    const g = this.g, f = g.flow;
    if (f.scene !== Scene.Dungeon) return;
    if (!this.holdReleased) {
      if (!this.holdStarted) {
        const boss = g.boss.state !== BossState.Inactive;
        this.beginClearHold(boss ? g.boss.x : g.hero.x, boss ? g.boss.z : g.hero.z, false);
      }
      this.clearPending = true;
      return;
    }
    this.stageClear();
  }

  /** 건너뛰기: 대기가 skipAfter 이상 흐른 뒤에만 받는다. 보스 사망 연출이 남아 있어도 바로 결과로 넘긴다 */
  skipClearHold(): boolean {
    const f = this.g.flow;
    if (f.clearHold <= 0 || f.scene !== Scene.Dungeon) return false;
    if (f.clearHoldTotal - f.clearHold < CLEAR_HOLD.skipAfter) return false;
    // 보스 사망 연출 중(Dying·Dead)이거나 마지막 몹 정리가 끝나 넘어갈 차례(clearPending)일 때만. 스페셜 몹이 남은 판은 넘기지 않는다
    const bs = this.g.boss.state;
    if (!this.clearPending && bs !== BossState.Dying && bs !== BossState.Dead) return false;
    f.clearHold = 0; this.holdReleased = true;
    this.stageClear();
    return true;
  }

  private clearPending = false;

  stageClear(): void {
    const g = this.g, f = g.flow, r = g.result, h = g.hero;
    if (f.scene !== Scene.Dungeon) return;
    this.clearPending = false;
    f.clearHold = 0;
    if (this.plan.raid) { this.raidClear(this.plan.raid, this.plan.raidDiff ?? "normal"); return; }
    if (this.plan.daily) { g.dailyRun.dailyClear(); return; }
    if (g.trialRun.running) { g.trialRun.trialClear(); return; }
    if (f.roomIndex >= 0) {
      const room = this.plan.rooms[f.roomIndex];
      f.roomState[f.roomIndex] = 2;
      // 보스방은 RoomClear 표시는 남기되 방 사이 회복은 적용하지 않는다.
      if (room.kind === "battle") {
        const missing = Math.max(0, g.hero.maxHp - g.hero.hp);
        const amount = Math.floor(missing * ROOM_HEAL.missingRatio);
        if (amount > 0) { g.hero.hp += amount; const e = g.emit(Ev.HeroHeal); e.x = g.hero.x; e.z = g.hero.z; e.a = amount; e.b = 0; }
      }
      g.emit(Ev.RoomClear).a = f.roomIndex;
    }
    f.objectiveKilled = f.objectiveTotal;
    g.emit(Ev.StageClear);
    r.cleared = true; r.clearTicks = f.elapsedTicks; r.maxCombo = g.combo.best;
    r.starMask = StarBit.Clear | (r.clearTicks <= STAR_TIME_TICKS ? StarBit.Time : 0) | (h.hp >= h.maxHp * STARS.healthRatio ? StarBit.Health : 0);
    r.stars = (r.starMask & 1) + ((r.starMask >> 1) & 1) + ((r.starMask >> 2) & 1);
    const plan = this.plan;
    const reward = g.map.id === "village" ? REWARD : REGIONS[plan.region].reward;
    // 장비 골드 획득 옵션(BUFF_CAP +100%까지, 던전 입장 때 고정): 클리어 골드와 카드 골드에 곱한다. 난수 소비 순서는 그대로
    // 난이도 골드 배율(하드 ×3, economy DIFFICULTY_LOOT — D3b)은 클리어·카드 골드에 함께 곱한다
    const gm = g.stats.goldMult * plan.goldDiff;
    // S3 스테이지 보상 차등: 클리어 base·카드 골드에 스테이지 종류 배율(일반 0.95·n-5 2·n-10 3.5), 처치 골드는 그대로. 자유 입장은 배율 없음
    const cm = clearMult(baseStageOf(plan.stage));
    // 첫 클리어 보상(노말 스테이지별 1회, 하드 첫 클리어 보상은 후속): 골드는 난이도 배율 없이 이번 결과 골드에 합쳐 하나의 v1 쓰기로 확정한다.
    // 장비·강화석은 아래에서 장부 연산(고정 id)으로
    // 하드 첫 클리어 보상도 노말과 같은 표(firstClearReward)를 쓰고 골드에는 난이도 배율을 곱하지 않는다(시뮬 가정). 강화석 등급만 하드 아이템 레벨을 따른다
    // 헬(C10)도 같은 길: 헬 진행 저장의 첫 클리어 기록, 강화석 등급은 헬 아이템 레벨(+20)
    const dstore = this.diffStore(plan.diff);
    const base = plan.stage ? baseStageOf(plan.stage) : null;
    const due = plan.stage && base ? (dstore ? dstore.firstRewardDue(plan.stage) : g.progress.firstRewardDue(plan.stage as StageId)) : false;
    const fc = due && base ? firstClearReward(base, dstore ? (plan.diff as "hard" | "hell") : "normal") : null;
    // 첫 클리어 골드는 결과 골드(무작위 결과 id)에 합치지 않고 스테이지 고정 id 보너스 영수증(`first:<스테이지 id>`)으로 지급한다 —
    // 두 탭이 같은 스테이지를 동시에 깨거나 하드 진행 쓰기 실패 뒤 새로고침해서 다시 깨도 같은 id라 골드는 한 번만 들어간다(PR #175 4차 리뷰).
    // 이미 그 영수증이 있으면(전에 받았다) 골드만 다시 주지 않는다. 장비·강화석은 고정 연산 id라 장부가 거른다
    const firstId = plan.stage ? firstGoldId(plan.stage) : "";
    const goldPaid = firstId !== "" && g.save.hasResult(firstId);
    r.clearMult = cm; r.firstGold = fc && !goldPaid ? Math.round(fc.gold * g.stats.goldMult) : 0; r.firstStones = 0; r.firstStoneTier = 0; r.firstGear.length = 0;
    r.gold = Math.round((reward.base * cm + r.kills * reward.perKill) * gm) + r.firstGold;
    // 카드 굴림 기준값(난수 소비는 그대로): 골드 카드 액수는 옛 식(×장비 골드 획득 옵션 포함), 골드가 아닌 카드의 환산 예산에는 옵션 배율을 곱하지 않는다
    const cardBase = [0, 0, 0];
    for (let i = 0; i < r.cards.length; i++) { const raw = g.rng.int(reward.cardMin, reward.cardMax + 1); cardBase[i] = raw * cm * plan.goldDiff; r.cards[i] = Math.round(raw * cm * gm); }
    // 스페셜 몬스터 처치 골드(황금 고블린 base×1·챔피언 base×0.5)는 처치 순간 이미 영속 지급했다(game/special.ts payGold).
    // 여기서는 클리어 골드·영수증에 더하지 않고(이중 지급 방지) 결과 화면 '스페셜 +N' 항목용 금액만 담는다
    r.specialGold = g.special.paidGold;
    // 기본 보상·기록·카드 후보는 하나의 v1 쓰기로 확정한다.
    r.resultId = rewardId();
    // 보상 카드 내용: 골드 카드의 액수는 위 굴림 그대로(기존 범위 안), 나머지 카드는 같은 예산을 강화석·보석·소비·장비로 바꾼다(content/cardReward — 영수증 id가 씨앗이라 새로고침해도 같은 카드)
    {
      const kind = plan.kind === "mid" || plan.kind === "final" ? plan.kind : "normal";
      const goldAmount = [r.cards[0], r.cards[1], r.cards[2]];
      const rolled = rollCards({ region: Math.max(1, Math.min(6, plan.regionNo)) as 1 | 2 | 3 | 4 | 5 | 6, diff: plan.diff, kind, budget: cardBase, goldAmount, seed: r.resultId,
        budgetRange: [Math.round(reward.cardMin * cm * gm), Math.round(reward.cardMax * cm * gm)], gearCtx: g.loot.cardGearCtx() });
      for (let i = 0; i < 3; i++) { r.cards[i] = rolled.gold[i]; r.cardLoot[i] = rolled.loot[i]; }
    }
    // 지역 클리어 EXP(이번 판 EXP에 더한다). 레벨이 오르면 같은 자리에서 LevelUp이 난다
    // 스테이지 판: 보스가 없는 일반 스테이지는 보스 처치 EXP를 클리어에 함께 준다(판마다 지역 고정 보상 = 보스 + 클리어, 시뮬 pacing 가정과 같다)
    const bossless = plan.stage !== null && plan.kind === "normal";
    g.grantXp(XP_CLEAR[plan.region] + (bossless ? XP_BOSS[plan.region] : 0), XpCause.Clear, h.x, h.z);
    // 첫 클리어 장비·강화석: 장부에 넣고 저장을 시도한다. 결과 화면 표시 필드(firstStones·firstGear)가 정해지므로 영수증보다 먼저 한다.
    // 보상 내용은 아래 진행 기록(recordClear)에 "받는 중"으로 함께 적는다 — 수령 기록은 장비 장부·영수증 저장 확인 뒤(settleFirstClears, PR #175 리뷰 P1)
    const owed = fc && plan.stage ? this.grantFirstClear(plan.stage, fc, r, firstId) : undefined;
    g.save.recordResult({ id: r.resultId, region: plan.region, baseGold: r.gold - r.firstGold,
      clearTicks: r.clearTicks, maxCombo: r.maxCombo, stars: r.stars, starMask: r.starMask,
      kills: r.kills, damageDealt: r.damageDealt, cards: Array.from(r.cards), pickedCard: -1, cardLoot: Array.from(r.cardLoot), ...(plan.stage ? { stage: plan.stage } : {}),
      // 결과 화면 표시 필드(선택): 카드를 고르기 전에 새로고침해도 보상 내역이 그대로 복원된다
      ...(plan.stage ? { clearMult: r.clearMult } : {}),
      ...(r.specialGold > 0 ? { specialGold: r.specialGold } : {}),
      ...(fc ? { firstGold: r.firstGold, firstStones: r.firstStones, firstStoneTier: r.firstStoneTier, firstGear: Array.from(r.firstGear) } : {}) });
    if (r.firstGold > 0) this.recordFirstGold(firstId, plan.region, r.firstGold);
    // 스테이지 진행(D3a): 순서대로 다음 스테이지를 처음 깨면 해금이 오르고, n-10 첫 클리어면 다음 마을 이동을 예약한다
    r.stageId = plan.stage ?? ""; r.firstClear = false; r.travelTo = 0; r.nextStage = "";
    if (plan.stage && dstore) {
      // 하드·헬 판(D3b·C10): 그 난이도 진행에만 기록한다(노말 진행·마을 이동은 그대로). 다음 = 같은 난이도 다음 스테이지가 열렸으면
      const rec = dstore.recordClear(plan.stage as HardStageId | HellStageId, r.stars, owed);
      r.firstClear = rec.first;
      // 진행 저장 실패(쓰기·확인 실패, 미래 버전 읽기 전용)는 결과 화면 저장 경고로 알리고 tickSave가 다시 쓴다
      this.hardSavePending = !dstore.lastSaveOk;
      const next = nextStageOf(plan.stage);
      r.nextStage = next && dstore.unlocked(next) ? next : "";
      const e = g.emit(Ev.StageResult); e.a = plan.regionNo; e.b = plan.index; e.c = rec.first ? 1 : 0; e.d = 0;
    } else if (plan.stage) {
      const rec = g.progress.recordClear(plan.stage as StageId, r.stars, owed);
      // 노말 진행 저장 실패도 하드처럼 결과 저장 경고로 알리고 tickSave가 다시 쓴다(PR #175 3차 리뷰 P2)
      this.progressSavePending = !g.progress.lastSaveOk;
      r.firstClear = rec.first; r.travelTo = rec.travel;
      const next = nextStageOf(plan.stage);
      r.nextStage = next && g.progress.unlocked(next as StageId) ? next : "";
      const e = g.emit(Ev.StageResult); e.a = plan.regionNo; e.b = plan.index; e.c = rec.first ? 1 : 0; e.d = rec.travel;
    }
    // 같은 결과 ID로 EXP 영수증을 기록한다(판 한 번에 한 번)
    g.growth.commitRun(r.resultId);
    // 바닥에 남은 드롭을 거두고(클리어 수거) 장비 장부를 저장한다
    g.loot.sweep();
    g.gear.flush(true);
    r.savePending = g.save.savePending || this.hardSavePending || this.progressSavePending;
    r.saveRecovered = g.save.recovered > 0;
    g.profile.sync();
    f.scene = Scene.Result; f.sceneTick = 0; f.paused = false; f.objectiveLabel = "보상 카드를 선택하세요.";
    h.act = HeroAct.Victory; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.hitstop = 0;
    g.awakening.cancel();
    g.mobController.reset();
    g.releaseAllTelegraphs();
    g.skillFx.clear();
    g.arrows.clear();
    g.emit(Ev.SceneChange).a = Scene.Result;
  }

  /**
   * 이번 주 받은 레이드 보상 횟수(RAID-PRD 2절). 로컬 영수증 id ∪ 서버 장부(넷 판·올린 솔로 영수증, C1a game/raidLedger.ts).
   * 서버 장부를 모르면(오프라인·게스트·레이드 넷 꺼짐) 로컬 영수증만 — 예전 동작
   */
  raidClears(raid: RaidId): number {
    const g = this.g, week = g.raidWeek();
    return raidUnifiedDone(raidWeekReceipts((id) => g.save.hasResult(id), raid, week), g.raidServer, raid, week);
  }

  /**
   * 레이드 클리어 정산(RAID-PRD 6절): 이번 주 보상이 남았으면 n번째 영수증(고정 id, 골드 포함)과 장비 장부 연산(세트 부위·전설·강화석·보호 주문서)을
   * 넣는다. 다 받았으면 연습(보상 없음, EXP만). 카드 고르기 없이 결과 화면으로 간다(pickedCard 0 = 확정)
   */
  private raidClear(raid: RaidId, diff: RaidDiff = "normal"): void {
    const g = this.g, f = g.flow, r = g.result, h = g.hero;
    if (f.roomIndex >= 0) { f.roomState[f.roomIndex] = 2; g.emit(Ev.RoomClear).a = f.roomIndex; }
    f.objectiveKilled = f.objectiveTotal;
    g.emit(Ev.StageClear);
    r.cleared = true; r.clearTicks = f.elapsedTicks; r.maxCombo = g.combo.best;
    r.starMask = StarBit.Clear | (r.clearTicks <= STAR_TIME_TICKS ? StarBit.Time : 0) | (h.hp >= h.maxHp * STARS.healthRatio ? StarBit.Health : 0);
    r.stars = (r.starMask & 1) + ((r.starMask >> 1) & 1) + ((r.starMask >> 2) & 1);
    r.cards.fill(0); r.pickedCard = 0;
    // 다른 탭이 같은 주 n 자리를 다른 난이도 영수증으로 먼저 채웠을 수 있다(노말·하드 id가 달라 같은 id 멱등이 막아 주지 않는다 — #336 리뷰 P1).
    // 저장소 최신 영수증을 먼저 읽고 센다(#334 jobRun과 같은 방식). 같은 순간 두 탭의 동시 정산 경합은 남는다(docs/RAID-HARD.md 4절)
    g.save.refresh();
    const def = raidDef(raid), week = g.raidWeek(), done = this.raidClears(raid);
    r.raid = raid; r.raidLimit = def.weeklyLimit; r.raidLeft = 0; r.stageId = raidStageKey(raid, diff); r.raidDiff = diff; r.raidFirst = false;
    // 하드 첫 클리어(C13): 주 보상과 따로, 영원히 한 번(고정 영수증 raidFirstId). 칭호는 이 영수증으로 보인다
    if (diff === "hard" && raidHardDef(raid)) {
      const fid = raidFirstId(raid, "hard");
      // 서버 장부에 이 캐릭터의 첫 클리어가 이미 있다(넷 판이나 다른 기기 — C1a): 확정 전설은 다시 주지 않고 칭호 표시 영수증(골드 0)만 남긴다.
      // 넷 판 끝(raidLink.finish)과 같은 순서 — 정산 표시를 먼저 써야 재접속 정산(settleRaids)이 전설을 다시 넣지 않는다
      if (!g.save.hasResult(fid) && raidServerFirst(g.raidServer, raid) && g.raidStore.claim(fid)) {
        g.save.recordResult({ id: fid, region: "r2", baseGold: 0, clearTicks: r.clearTicks, maxCombo: r.maxCombo, stars: r.stars, starMask: r.starMask,
          kills: 0, damageDealt: r.damageDealt, cards: [0, 0, 0], pickedCard: 0, bonus: true, raid, raidHero: g.heroId, raidN: 0, raidProtect: 0 });
      }
      if (!g.save.hasResult(fid)) {
        const fw = raidFirstReward(raid, g.heroId as HeroId, g.stats.goldMult)!;
        g.save.recordResult({ id: fid, region: "r2", baseGold: fw.gold, clearTicks: r.clearTicks, maxCombo: r.maxCombo, stars: r.stars, starMask: r.starMask,
          kills: 0, damageDealt: r.damageDealt, cards: [0, 0, 0], pickedCard: 0, bonus: true, raid, raidHero: g.heroId, raidN: 0, raidProtect: 0 });
        for (const op of fw.ops) g.gear.submit(op);
        if (g.gear.flush(true) && !g.save.savePending) g.raidStore.claim(fid);
        r.raidFirst = true; r.raidFirstGold = fw.gold; r.raidFirstLegend = fw.legend; r.raidFirstStones = fw.stones;
      }
    }
    // n = 합친 셈 + 1(그 자리 영수증이 이미 있으면 가장 작은 빈 자리). 0이면 연습
    const n = raidNextN((id) => g.save.hasResult(id), raid, week, done, g.raidServer);
    if (n > 0) {
      const rid = raidReceiptId(raid, week, n, diff);
      const protectOk = g.gear.protectUnlocked;
      const rw = raidReward(raid, week, n, g.heroId as HeroId, protectOk, g.stats.goldMult, diff);
      // 남은 횟수 = 한도 − (합친 셈 + 이번 판). n은 빈 자리 번호라 구멍을 메우면 n보다 셈이 크다
      r.resultId = rid; r.raidN = n; r.raidLeft = Math.max(0, def.weeklyLimit - (done + 1)); r.gold = rw.gold;
      r.raidSet = rw.set; r.raidLegend = rw.legend; r.raidStones = rw.stones; r.raidStoneTier = rw.stoneTier; r.raidProtect = rw.protect;
      g.save.recordResult({ id: rid, region: "r2", baseGold: rw.gold, clearTicks: r.clearTicks, maxCombo: r.maxCombo, stars: r.stars, starMask: r.starMask,
        kills: 0, damageDealt: r.damageDealt, cards: [0, 0, 0], pickedCard: 0, bonus: true, raid, raidHero: g.heroId, raidN: n, raidProtect: rw.protect });
      for (const op of rw.ops) g.gear.submit(op);
      if (g.gear.flush(true) && !g.save.savePending) g.raidStore.claim(rid);
    } else {
      r.resultId = rewardId(); r.raidN = 0; r.gold = 0;
    }
    const e = g.emit(Ev.StageResult); e.a = 2; e.b = 0; e.c = r.raidN; e.d = 0;
    // 레이드 EXP(지역2 보스 처치 EXP × 3) — 연습 판도 받는다
    g.grantXp(XP_BOSS.r2 * RAID_XP_MULT, XpCause.Boss, h.x, h.z);
    g.growth.commitRun(r.resultId);
    g.gear.flush(true);
    r.savePending = g.save.savePending || this.raidPayoutPending();
    r.saveRecovered = g.save.recovered > 0;
    g.profile.sync();
    f.scene = Scene.Result; f.sceneTick = 0; f.paused = false;
    f.objectiveLabel = r.raidFirst ? "하드 첫 클리어! 칭호와 확정 전설을 얻었어요." : r.raidN > 0 ? "레이드 보상 획득! 마을로 돌아가거나 다시 도전하세요." : "이번 주 레이드 보상을 모두 받았어요(연습).";
    h.act = HeroAct.Victory; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.hitstop = 0;
    g.awakening.cancel();
    g.mobController.reset();
    g.releaseAllTelegraphs();
    g.skillFx.clear();
    g.arrows.clear();
    g.emit(Ev.SceneChange).a = Scene.Result;
  }

  /**
   * 레이드 보상 정산(재접속·저장 실패 뒤): 주와 상관없이 남은 레이드 영수증 중 정산 표시가 없는 것의 보상 연산을 같은 id로 다시 넣고,
   * 장비 장부 저장이 확인되면 표시한다. 같은 연산은 장부가 거르므로 몇 번을 불러도 보상은 한 번이다(RAID-PRD 2절)
   */
  settleRaids(): void {
    const g = this.g;
    // 정산할 것이 보일 때만 다른 탭 표시를 다시 읽는다(일반 틱은 저장소를 읽지 않는다 — rpg.test 불변식)
    let refreshed = false;
    // 주와 상관없이 save.v1에 남은 모든 레이드 영수증(몇 주 뒤에 다시 접속해도 받지 못한 장비·강화석을 넣는다, Codex P2 #207)
    g.save.forEachRaidReceipt((id) => {
      if (g.raidStore.isClaimed(id)) return;
      if (!refreshed) { refreshed = true; g.raidStore.refresh(); if (g.raidStore.isClaimed(id)) return; }
      const at = parseRaidReceipt(id), rec = g.save.getResult(id);
      if (!at || !rec) return;
      const hero = (rec.raidHero === "lyra" ? "lyra" : "kael") as HeroId;
      const rw = at.first ? raidFirstReward(at.raid, hero) : raidReward(at.raid, at.week, at.n, hero, (rec.raidProtect ?? 0) > 0, 1, at.diff);
      if (!rw) return;
      for (const op of rw.ops) g.gear.submit(op);
      if (g.gear.flush(true) && !g.save.savePending) g.raidStore.claim(id);
    });
    this.refreshRaidWeek();
  }

  /** 레이드 결과의 장비 보상이 아직 장비 장부에 저장되지 않았다(결과 화면 저장 대기 표시, Codex P2 #207) */
  private raidPayoutPending(): boolean {
    const r = this.g.result;
    // 넷 판 결과(net:<runId>)는 서버가 우편함으로 정산한다 — 솔로 장부 대기가 아니다
    if (r.resultId.startsWith("net:")) return false;
    if (!r.raid) return false;
    if ((r.raidN ?? 0) > 0 && !this.g.raidStore.isClaimed(r.resultId)) return true;
    // 하드 첫 클리어 영수증도 장비 저장 전이면 대기다(주 3회 소진 뒤 첫 클리어 = raidN 0, #336 Codex P2)
    return !!r.raidFirst && !this.g.raidStore.isClaimed(raidFirstId(r.raid as RaidId, "hard"));
  }

  /** 레이드 밖(마을 지휘소 창): 이번 주 받은 보상 횟수를 레이드 뷰에 옮긴다(판 안에서는 입장 때 값을 쓴다) */
  refreshRaidWeek(): void {
    const g = this.g, s = g.raid.state;
    if (s.active) return;
    const raid = PLAYABLE_RAIDS[0];
    s.weekClears = this.raidClears(raid); s.weekLimit = raidDef(raid).weeklyLimit; s.practice = s.weekClears >= s.weekLimit;
    s.hardOpen = hardUnlocked(raid, g.maxRebirthCycle()); s.hardFirst = !!raidHardDef(raid) && (g.save.hasResult(raidFirstId(raid, "hard")) || raidServerFirst(g.raidServer, raid));
  }

  defeat(): void {
    const g = this.g, f = g.flow, h = g.hero;
    // 파티 판(레인 D): 패배 화면 없이 그 자리에서 쓰러졌다가 일어난다(동료가 계속 싸운다). 부활 깃털은 쓰지 않는다
    // 레이드 넷 판: 쓰러진 채 서버가 일으켜 주기를 기다린다(레이드 실패가 아니다). 부활 깃털은 쓰지 않는다
    if (g.raidNet.active) { g.raidNet.heroDown(); return; }
    if (g.party.active) { g.party.heroDown(); return; }
    // 부활 깃털(D2c): 이번 판에 아직 안 썼고 가지고 있으면 쓰러지지 않고 HP 50%로 일어난다
    if (g.consumables.tryRevive()) return;
    // 패배 목표 문구는 두 버튼(다시 도전·마을로)만 가리킨다. M1-P 마을에는 정비·강화가 없다(UX-FLOW 패배 분기).
    f.scene = Scene.Defeat; f.sceneTick = 0; f.paused = false; f.objectiveLabel = "다시 도전하거나 마을로 돌아가세요.";
    h.act = HeroAct.Dead; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.result.cleared = false; g.result.clearTicks = f.elapsedTicks; g.result.maxCombo = g.combo.best;
    g.awakening.cancel();
    g.releaseAllTelegraphs();
    g.skillFx.clear();
    g.arrows.clear();
    // 패배해도 이번 판 EXP는 남는다(벌칙 없음). 판 고유 ID로 한 번 기록한다
    g.growth.commitRun();
    // 이미 주운 장비는 남는다(바닥 드롭은 사라진다)
    g.gear.flush(true);
    g.emit(Ev.Defeat);
    g.emit(Ev.SceneChange).a = Scene.Defeat;
    if (this.plan.daily) g.dailyRun.dailyFail();
    if (g.trialRun.running) g.trialRun.trialDefeat();
  }

  pickCard(index: number): void {
    const g = this.g, r = g.result;
    if (g.flow.scene !== Scene.Result || !r.cleared) return;
    if (r.pickedCard !== -1) { g.save.flush(); this.syncResult(); return; }
    if (!Number.isInteger(index) || index < 0 || index >= r.cards.length) return;
    // 다른 탭의 먼저 확정된 선택을 우선한다. 선택과 골드는 같은 쓰기로 정산한다.
    g.save.pickResult(r.resultId, index);
    this.syncResult();
    // 골드가 아닌 카드(강화석·보석·소비·장비)의 장비 장부 지급은 여기서 하지 않는다: 이 탭이 본 선택이 저장에서 이기지 못할 수 있다(두 탭 경합).
    // 저장으로 확정된 영수증을 보고 settleCardLoots가 지급한다(저장 확인 틱·재접속·같은 탭 동기 저장 직후)
    this.settleCardLoots();
    g.profile.sync();
    g.flow.objectiveLabel = "보상 획득! 마을로 돌아가세요.";
  }

  // ── QA 픽스처(?qa 전용). 정상 플레이 증거와 구분해 GameSim.qaOps에 센다 ──

  /** 방 index 입구 안쪽으로 순간이동. 앞 방은 정리된 상태, 문은 그에 맞게 둔다 */
  qaGotoRoom(index: number): boolean {
    const g = this.g, f = g.flow, rooms = g.map.rooms;
    if (!Number.isInteger(index) || index < 0 || index >= rooms.length) return false;
    g.mobController.reset();
    g.bossController.reset();
    g.releaseAllTelegraphs();
    this.resetWaves();
    this.resetGates();
    for (let k = 0; k < rooms.length; k++) {
      f.roomState[k] = k < index ? 2 : 0;
      if (k < index) {
        this.setGateNow(rooms[k].gateIn, false);
        this.setGateNow(rooms[k].gateOut, true);
      }
    }
    f.roomIndex = -1; f.stageLabel = g.map.name; f.objectiveLabel = "전투방으로 이동하세요.";
    const area = rooms[index].area;
    g.snapHero((area.x0 + area.x1) / 2, area.z1 - ROOM_ENTER_INSET - 0.5);
    g.hero.facing = Math.PI;
    return true;
  }

  private setGateNow(index: number, open: boolean): void {
    if (index < 0) return;
    const v = open ? 1 : 0;
    this.gateTarget[index] = v; this.gateFrom[index] = v; this.gateTick[index] = GATE_TICKS; this.g.flow.gateOpen[index] = v;
  }

  /** 현재 전투방의 다음 웨이브를 즉시 시작 */
  qaSpawnWave(): boolean {
    const f = this.g.flow, room = this.plan.rooms[f.roomIndex];
    if (!room || room.kind !== "battle" || f.roomState[f.roomIndex] !== 1 || f.wave + 1 >= room.waves.length) return false;
    this.startWave(f.wave + 1);
    return true;
  }

  /** 현재 몹 전부 처치(처치 수·목표·Kill 이벤트는 정상 경로로 기록) */
  qaKillAll(): void {
    const g = this.g, m = g.mobs;
    for (let i = 0; i < m.capacity; i++) {
      if (m.state[i] !== MobState.Inactive && m.state[i] !== MobState.Dying) g.combat.killMob(i, HitSource.Basic);
    }
  }
}
